<template>
  <div class="search-info-panel">
    <ElInput
      v-model="keyword"
      placeholder="搜索"
      clearable
      size="small"
      class="search-input"
      :class="{ 'search-input--sticky': useExternalScroll }"
    >
      <template #prefix>
        <el-icon><Search /></el-icon>
      </template>
    </ElInput>
    <div
      class="search-results"
      :class="{ 'search-results--external': useExternalScroll }"
      @scroll="useExternalScroll ? undefined : handleScroll"
    >
      <div
        v-for="(item, index) in displayResults"
        :key="item?._id || index"
        class="result-item"
        @click="handleClickResult(item)"
      >
        <div class="result-date">{{ item.date }}</div>
        <div class="result-content" v-html="highlightText(item.content)" />
      </div>
      <div v-if="hasMore" class="load-more-tip">
        加载更多...
      </div>
      <div v-if="filteredResults.length === 0" class="empty-tip">
        暂无搜索结果
      </div>
    </div>
  </div>
</template>

<script>
import { Search } from "@element-plus/icons-vue";

export default {
  name: "SearchInfoPanel",
  components: { Search },
  emits: ['load-more-history', 'select-result'],
  props: {
    results: {
      type: Array,
      default: () => [],
    },
    messagesLoaded: {
      type: Boolean,
      default: true,
    },
    useExternalScroll: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      keyword: "",
      pageSize: 50,
      page: 1,
      emittingLoadHistory: false,
    };
  },
  computed: {
    filteredResults() {
      if (!this.keyword.trim()) return this.results;
      const kw = this.keyword.trim().toLowerCase();
      return this.results.filter((r) =>
        (r.content || "").toLowerCase().includes(kw)
      );
    },
    displayResults() {
      const end = this.page * this.pageSize;
      return (this.filteredResults || []).slice(0, end);
    },
    hasMore() {
      return (this.filteredResults || []).length > (this.displayResults || []).length;
    },
    canLoadHistory() {
      return !this.messagesLoaded;
    }
  },
  watch: {
    keyword() {
      this.page = 1;
      this.emittingLoadHistory = false;
      this.$nextTick(() => {
        const el = this.$el?.querySelector?.('.search-results')
        if (el) el.scrollTop = 0
      })
    },
    results() {
      // 数据源变化（切换房间/加载更多消息）时，保持当前 page，确保显示尽量平滑
      // 但要保证 page 不超过最大页
      const total = (this.filteredResults || []).length
      const maxPage = Math.max(1, Math.ceil(total / this.pageSize))
      if (this.page > maxPage) this.page = maxPage

      // 父级加载到更多历史消息后，允许再次触发 load history
      this.emittingLoadHistory = false
    }
  },
  methods: {
    handleClickResult(item) {
      if (!item) return
      this.$emit('select-result', item)
    },
    onReachBottom() {
      this.loadMoreOrFetchHistory()
    },
    highlightText(text) {
      if (!text || !this.keyword.trim()) return escapeHtml(text || "");
      const kw = this.keyword.trim();
      const escaped = escapeHtml(text);
      const regex = new RegExp(`(${escapeRegex(kw)})`, "gi");
      return escaped.replace(regex, '<mark class="highlight">$1</mark>');
    },
    handleScroll(e) {
      const target = e?.target
      if (!target) return
      const scrollBottom = target.scrollTop + target.clientHeight
      if (scrollBottom >= target.scrollHeight - 20) {
        this.loadMoreOrFetchHistory()
      }
    },
    loadMoreOrFetchHistory() {
      // 1) 本地还有更多：直接分页显示
      if (this.hasMore) {
        this.page += 1
        return
      }

      // 2) 本地到底了，但外部还有历史：触发拉取更老消息
      if (this.canLoadHistory && !this.emittingLoadHistory) {
        this.emittingLoadHistory = true
        this.$emit('load-more-history')
      }
    }
  },
};

function escapeHtml(str) {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
</script>

<style lang="scss" scoped>
.search-info-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  .search-input {
    flex-shrink: 0;
    margin-bottom: 16px;

    :deep(.el-input__wrapper) {
      width: 100%;
    }
  }

	.search-input.search-input--sticky {
		position: sticky;
		top: 0;
		z-index: 3;
		background: #fff;
		padding-top: 12px;
		margin-top: -12px;
	}

  .search-results {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .search-results.search-results--external {
    flex: 0 0 auto;
    overflow: visible;
  }

  .load-more-tip {
    padding: 12px 0;
    text-align: center;
    font-size: 12px;
    color: #999;
  }

  .result-item {
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  .result-date {
    font-size: 12px;
    color: #999;
    margin-bottom: 6px;
  }

  .result-content {
    font-size: 13px;
    color: #333;
    line-height: 1.5;

    :deep(.highlight) {
      background: #fffbe6;
    }
  }

  .empty-tip {
    padding: 40px 0;
    text-align: center;
    font-size: 13px;
    color: #999;
  }
}
</style>
